import {compact} from '@benchling/js-utils';
import React, {useMemo} from 'react';

import {MinimalRequestAssignee} from 'requests/RequestConstants';
import {MinimalEntity} from 'ui-library/entity-input/EntityInputConstants';
import {EntityInputWithSearch, UserSearchType} from 'ui-library/entity-input/EntityInputWithSearch';
import EditablePartyField from 'ui-library/inputs/EditablePartyField';
import {TagsInputSize} from 'ui-library/tags-input/TagsInputConstants';
import APIUtil from 'util/APIUtil';

interface EditableRequestAssigneesProps {
  assignees: Array<MinimalRequestAssignee>;
  onUpdateAssignees: (assignees: Array<MinimalRequestAssignee>) => void;
  organizationId: string;
  // whether or not to wrap the user / team search input in a BaseEditableField
  shouldChangeBeConfirmed: boolean;
  useLegacyEntityInput?: boolean;
}

export default function EditableRequestAssignees(props: EditableRequestAssigneesProps): JSX.Element {
  const value = useMemo(
    () => compact(props.assignees.map(({user, team}) => (user == null ? team : user))),
    [props.assignees]
  );

  const onChange = (newAssignees: Array<MinimalEntity>): void => {
    props.onUpdateAssignees(
      newAssignees.map((party) =>
        APIUtil.isTeam(party.id) ? {user: null, team: party} : {user: party, team: null}
      )
    );
  };

  const sharedInputProps = {
    enableNewEntityInput: !props.useLegacyEntityInput,
    allowEmails: false,
    allowTeams: true,
    allowOrganizations: false,
    placeholder: 'Search for assignee',
    shouldAllowMultiple: true as const, // explicitly cast to true because PartyProps expects true (not boolean)
    userSearchType: UserSearchType.NotSuspended,
  };

  return props.shouldChangeBeConfirmed ? (
    <EditablePartyField
      {...sharedInputProps}
      value={value}
      onChange={onChange}
      organizationIds={[props.organizationId]}
      isDisabled={false}
      chipSize={TagsInputSize.SMALL}
      isFullWidth={false}
      initialIsBeingEdited={false}
      allowEmptyValue={true}
      shouldUseCompactDisplay={true}
      noViewerPlaceholder={true}
    />
  ) : (
    <EntityInputWithSearch
      {...sharedInputProps}
      onChange={onChange}
      value={value}
      size={TagsInputSize.SMALL}
    />
  );
}
